import React, { useState, useEffect } from 'react';
import { 
  Mail, Send, Inbox, Search, FileText, User, Loader2, 
  ChevronDown, ChevronUp, Trash2, RefreshCw, CheckCircle, AlertCircle,
  Wand2, Sparkles, X, Paperclip, File, Check, Building2, MessageSquare,
  ExternalLink, Clock, UserCheck, Lock, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DossierMessaging = ({ dossier, documents, user }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [sending, setSending] = useState(false);
  const [notification, setNotification] = useState(null);
  const [destinataireSuggestions, setDestinataireSuggestions] = useState([]);
  const [departementInfo, setDepartementInfo] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [hasEmailAccount, setHasEmailAccount] = useState(false);
  
  // État pour l'assistant IA
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  
  // Formulaire
  const [formData, setFormData] = useState({
    to_email: '',
    to_name: '',
    subject: '',
    body: '',
    dossier_id: dossier?.id,
    include_client_email: true // Par défaut, inclure le mail du client
  });

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (dossier?.id) {
      loadEmails();
      loadTemplates();
      loadSuggestions();
      checkEmailAccount();
    }
  }, [dossier?.id]);

  // Vérifier si un compte email est configuré
  const checkEmailAccount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/email-accounts/default`, { headers });
      const data = await res.json();
      setHasEmailAccount(!!data && !!data.email);
    } catch (error) {
      console.error('Erreur vérification compte email:', error);
      setHasEmailAccount(false);
    }
  };

  const loadEmails = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/dossier/${dossier.id}/emails`, { headers });
      const data = await res.json();
      setEmails(data.emails || []);
    } catch (error) {
      console.error('Erreur chargement emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/templates`, { headers });
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Erreur chargement templates:', error);
    }
  };

  const loadSuggestions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging-config/suggestions/${dossier.id}`, { headers });
      const data = await res.json();
      setDestinataireSuggestions(data.suggestions || []);
      
      // Stocker les infos du département
      if (data.departement_info) {
        setDepartementInfo(data.departement_info);
      }
    } catch (error) {
      console.error('Erreur chargement suggestions:', error);
    }
  };

  // Générer le bloc d'identification
  const generateIdentificationBloc = () => {
    if (!dossier) return '';
    
    const identite = dossier.identite || {};
    const nom = identite.nom || dossier.nom || '';
    const prenom = identite.prenom || dossier.prenom || '';
    const dateNaissance = identite.date_naissance || dossier.date_naissance;
    const numeroSecu = identite.numero_secu || dossier.numero_secu || '';
    const adresse = identite.adresse || dossier.adresse || '';
    const codePostal = identite.code_postal || dossier.code_postal || '';
    const ville = identite.ville || dossier.ville || '';
    const telephone = identite.telephone || dossier.telephone || '';
    
    const lines = [];
    lines.push('IDENTIFICATION DU BÉNÉFICIAIRE');
    lines.push('─────────────────────────────');
    if (nom || prenom) {
      lines.push(`Nom : ${nom.toUpperCase()} ${prenom}`);
    }
    if (dateNaissance) {
      try {
        lines.push(`Date de naissance : ${new Date(dateNaissance).toLocaleDateString('fr-FR')}`);
      } catch (e) {
        lines.push(`Date de naissance : ${dateNaissance}`);
      }
    }
    if (numeroSecu) {
      lines.push(`N° Sécurité Sociale : ${numeroSecu}`);
    }
    if (adresse) {
      lines.push(`Adresse : ${adresse}`);
    }
    if (codePostal || ville) {
      lines.push(`         ${codePostal} ${ville}`);
    }
    if (telephone) {
      lines.push(`Téléphone : ${telephone}`);
    }
    lines.push('─────────────────────────────');
    
    return lines.join('\n');
  };

  // Ouvrir le formulaire de composition
  const openCompose = (templateId = null, toClient = false) => {
    const bloc = generateIdentificationBloc();
    
    setFormData({
      to_email: toClient && dossier.email ? dossier.email : '',
      to_name: toClient ? `${dossier.prenom} ${dossier.nom}` : '',
      subject: '',
      body: toClient ? '' : (bloc + '\n\n'),
      dossier_id: dossier.id,
      include_client_email: !toClient // Si on écrit au client, pas besoin de son email en CC
    });
    setSelectedDocuments([]);
    setShowCompose(true);
    setSelectedEmail(null);
    setShowAiAssistant(false);
    setAiDraft(null);
    
    if (templateId) {
      applyTemplate(templateId);
    }
  };

  // Appliquer un template
  const applyTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/generate-template`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          template_type: templateId,
          dossier_id: dossier.id
        })
      });
      const data = await res.json();
      
      setFormData(prev => ({
        ...prev,
        subject: data.subject || prev.subject,
        body: data.body || prev.body
      }));
    } catch (error) {
      console.error('Erreur template:', error);
    }
  };

  // Sélectionner un destinataire suggéré
  const selectDestinataire = (dest) => {
    setFormData(prev => ({
      ...prev,
      to_email: dest.email,
      to_name: dest.name
    }));
  };

  // Toggle document
  const toggleDocument = (doc) => {
    setSelectedDocuments(prev => {
      const isSelected = prev.some(d => d.id === doc.id);
      if (isSelected) {
        return prev.filter(d => d.id !== doc.id);
      } else {
        return [...prev, doc];
      }
    });
  };

  // Générer avec IA
  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      setNotification({ type: 'error', message: 'Décrivez ce que vous voulez dire' });
      return;
    }
    
    setAiGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/ai-reformulate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt: aiPrompt,
          dossier_id: dossier.id,
          subject: formData.subject
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setAiDraft({
          subject: data.subject || formData.subject,
          body: data.body
        });
      } else {
        setNotification({ type: 'error', message: data.detail || 'Erreur IA' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Erreur lors de la génération' });
    } finally {
      setAiGenerating(false);
    }
  };

  // Valider le brouillon IA
  const acceptAiDraft = () => {
    if (aiDraft) {
      setFormData(prev => ({
        ...prev,
        subject: aiDraft.subject,
        body: aiDraft.body
      }));
      setAiDraft(null);
      setShowAiAssistant(false);
      setAiPrompt('');
      setNotification({ type: 'success', message: 'Mail copié dans le formulaire' });
    }
  };

  // Envoyer l'email
  const sendEmail = async () => {
    if (!formData.to_email || !formData.subject || !formData.body) {
      setNotification({ type: 'error', message: 'Veuillez remplir tous les champs obligatoires' });
      return;
    }
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/send`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          attachments: selectedDocuments.map(d => d.id),
          include_client_email: formData.include_client_email && dossier.email
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setNotification({ type: 'success', message: 'Email envoyé avec succès !' });
        setShowCompose(false);
        loadEmails(); // Rafraîchir la liste
      } else {
        setNotification({ type: 'error', message: data.detail || 'Erreur lors de l\'envoi' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Erreur lors de l\'envoi' });
    } finally {
      setSending(false);
    }
  };

  // Supprimer un email
  const deleteEmail = async (emailId) => {
    if (!window.confirm('Supprimer cet email définitivement ?')) return;
    
    try {
      await fetch(`${API_URL}/api/messaging/email/${emailId}`, {
        method: 'DELETE',
        headers
      });
      setSelectedEmail(null);
      loadEmails();
      setNotification({ type: 'success', message: 'Email supprimé' });
    } catch (error) {
      setNotification({ type: 'error', message: 'Erreur lors de la suppression' });
    }
  };

  // Auto-dismiss notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  if (!dossier) return null;

  return (
    <div className="space-y-4">
      {/* Notification */}
      {notification && (
        <div className={`px-4 py-3 rounded-lg flex items-center gap-2 ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {notification.message}
          <button onClick={() => setNotification(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Actions rapides */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-semibold text-emerald-900">Actions rapides</h3>
            <p className="text-sm text-emerald-700">Envoyer un message concernant {dossier.prenom} {dossier.nom}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Bouton écrire au client */}
            {dossier.email && (
              <button
                onClick={() => openCompose(null, true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition"
              >
                <UserCheck className="w-4 h-4" />
                Écrire au client
              </button>
            )}
            
            {/* Template lien formulaire */}
            <button
              onClick={() => openCompose('lien_formulaire', true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition"
            >
              <ExternalLink className="w-4 h-4" />
              Envoyer lien formulaire
            </button>
            
            {/* Template documents */}
            <button
              onClick={() => openCompose('demande_documents', true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition"
            >
              <FileText className="w-4 h-4" />
              Demander documents
            </button>
            
            {/* Nouveau message organisme */}
            <button
              onClick={() => openCompose()}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition"
            >
              <Send className="w-4 h-4" />
              Écrire à un organisme
            </button>
          </div>
        </div>
      </div>

      {/* Infos département et organismes */}
      {(departementInfo || destinataireSuggestions.length > 0) && (
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-600" />
            Organismes à contacter
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Département */}
            {departementInfo && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🏛️</span>
                  <span className="font-medium text-blue-900">{departementInfo.name}</span>
                </div>
                {departementInfo.email && (
                  <p className="text-sm text-blue-700 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {departementInfo.email}
                  </p>
                )}
                {departementInfo.telephone && (
                  <p className="text-sm text-blue-700 mt-1">📞 {departementInfo.telephone}</p>
                )}
                {departementInfo.adresse && (
                  <p className="text-xs text-blue-600 mt-1">📍 {departementInfo.adresse}</p>
                )}
              </div>
            )}
            
            {/* Autres organismes suggérés */}
            {destinataireSuggestions.filter(s => s.type !== 'departement').map((org, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🏢</span>
                  <span className="font-medium text-gray-900">{org.name}</span>
                </div>
                {org.email && (
                  <p className="text-sm text-gray-700 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {org.email}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Zone de composition */}
      {showCompose && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              Nouveau message
            </h3>
            <button onClick={() => setShowCompose(false)} className="p-1 hover:bg-gray-200 rounded">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          <div className="p-4 space-y-4">
            {/* Suggestions de destinataires - Toujours afficher l'email du client */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Destinataires :</span>
              
              {/* Email du client en premier - toujours visible */}
              {(dossier.email || dossier.identite?.email) && (
                <button
                  onClick={() => {
                    const email = dossier.email || dossier.identite?.email;
                    const identite = dossier.identite || {};
                    const prenom = identite.prenom || dossier.prenom || '';
                    const nom = identite.nom || dossier.nom || '';
                    setFormData(prev => ({
                      ...prev,
                      to_email: email,
                      to_name: `${prenom} ${nom}`.trim(),
                      include_client_email: false,
                      body: '' // Pas de bloc identification quand on écrit au client
                    }));
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1 ${
                    formData.to_email === (dossier.email || dossier.identite?.email)
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300'
                  }`}
                >
                  <User className="w-3 h-3" />
                  {(dossier.identite?.prenom || dossier.prenom || 'Client')} (client)
                </button>
              )}
              
              {/* Autres suggestions d'organismes */}
              {destinataireSuggestions.map((dest, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    selectDestinataire(dest);
                    // Remettre le bloc identification quand on sélectionne un organisme
                    const bloc = generateIdentificationBloc();
                    setFormData(prev => ({
                      ...prev,
                      to_email: dest.email,
                      to_name: dest.name,
                      include_client_email: true,
                      body: prev.body.includes('IDENTIFICATION') ? prev.body : (bloc + '\n\n' + prev.body)
                    }));
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                    formData.to_email === dest.email
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-emerald-100'
                  }`}
                >
                  {dest.name}
                </button>
              ))}
            </div>
            
            {/* Champs destinataire */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email destinataire *</label>
                <input
                  type="email"
                  value={formData.to_email}
                  onChange={(e) => setFormData({ ...formData, to_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                  placeholder="email@exemple.fr"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom destinataire</label>
                <input
                  type="text"
                  value={formData.to_name}
                  onChange={(e) => setFormData({ ...formData, to_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                  placeholder="Nom de l'organisme"
                />
              </div>
            </div>
            
            {/* Option inclure email client */}
            {(dossier.email || dossier.identite?.email) && formData.to_email !== (dossier.email || dossier.identite?.email) && (
              <label className="flex items-center gap-2 text-sm text-gray-700 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                <input
                  type="checkbox"
                  checked={formData.include_client_email}
                  onChange={(e) => setFormData({ ...formData, include_client_email: e.target.checked })}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                <span>Mettre le client en copie ({dossier.email || dossier.identite?.email})</span>
              </label>
            )}
            
            {/* Objet */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Objet *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                placeholder="Objet du message"
              />
            </div>
            
            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                rows="16"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm leading-relaxed"
                placeholder="Votre message..."
              />
            </div>
            
            {/* Documents à joindre */}
            {documents && documents.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <Paperclip className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Pièces jointes</span>
                  {selectedDocuments.length > 0 && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                      {selectedDocuments.length} sélectionné(s)
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {documents.map(doc => {
                    const isSelected = selectedDocuments.some(d => d.id === doc.id);
                    return (
                      <button
                        key={doc.id}
                        onClick={() => toggleDocument(doc)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition ${
                          isSelected
                            ? 'bg-emerald-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:border-emerald-400'
                        }`}
                      >
                        {isSelected ? <Check className="w-3 h-3" /> : <File className="w-3 h-3" />}
                        {doc.nom || doc.type}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Assistant IA */}
            {showAiAssistant && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <Wand2 className="w-5 h-5 text-purple-600" />
                  <span className="font-medium text-purple-900">Assistant de rédaction</span>
                </div>
                
                {!aiDraft ? (
                  <>
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      rows="2"
                      placeholder="Décrivez ce que vous voulez dire..."
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm mb-2"
                    />
                    <button
                      onClick={generateWithAI}
                      disabled={aiGenerating || !aiPrompt.trim()}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition disabled:opacity-50"
                    >
                      {aiGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Générer
                    </button>
                  </>
                ) : (
                  <>
                    <div className="bg-white rounded-lg border border-purple-200 p-3 mb-2 max-h-40 overflow-y-auto">
                      <p className="text-xs text-gray-500 mb-1">Objet : {aiDraft.subject}</p>
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap">{aiDraft.body}</pre>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAiDraft(null)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Reformuler
                      </button>
                      <button
                        onClick={acceptAiDraft}
                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
                      >
                        <Check className="w-3 h-3" />
                        Valider
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Boutons d'action */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <button
                onClick={() => setShowAiAssistant(!showAiAssistant)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  showAiAssistant ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                }`}
              >
                <Wand2 className="w-4 h-4" />
                Rédiger avec IA
              </button>
              
              {/* Bouton copier le mail */}
              <button
                onClick={() => {
                  const mailContent = `À: ${formData.to_email}\nObjet: ${formData.subject}\n\n${formData.body}`;
                  navigator.clipboard.writeText(mailContent);
                  setNotification({ type: 'success', message: '✅ Mail copié ! Collez-le dans votre boîte mail personnelle.' });
                  setTimeout(() => setNotification(null), 4000);
                }}
                disabled={!formData.body}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50"
                title="Copier le mail pour l'envoyer depuis votre boîte personnelle"
              >
                <Copy className="w-4 h-4" />
                Copier
              </button>
              
              {/* Bouton envoyer - actif si compte email configuré */}
              {hasEmailAccount ? (
                <button
                  onClick={sendEmail}
                  disabled={sending || !formData.to_email || !formData.subject || !formData.body}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Envoyer
                </button>
              ) : (
                <div className="relative group">
                  <button
                    disabled={true}
                    className="flex items-center gap-2 px-6 py-2 bg-gray-400 text-white rounded-lg text-sm font-semibold cursor-not-allowed opacity-60"
                  >
                    <Lock className="w-4 h-4" />
                    Envoyer
                  </button>
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-10">
                    📧 Pas de compte email configuré.<br/>
                    Allez dans Configuration &gt; Comptes Email
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Historique des emails */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Inbox className="w-5 h-5 text-gray-600" />
            Historique des échanges ({emails.length})
          </h3>
          <button
            onClick={loadEmails}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        
        <div className="divide-y divide-gray-100">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Mail className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>Aucun email échangé pour ce dossier</p>
            </div>
          ) : (
            emails.map(email => (
              <div
                key={email.id}
                className={`p-4 hover:bg-gray-50 transition cursor-pointer ${
                  selectedEmail?.id === email.id ? 'bg-emerald-50' : ''
                }`}
                onClick={() => setSelectedEmail(selectedEmail?.id === email.id ? null : email)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {email.type === 'sent' ? (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Envoyé</span>
                      ) : (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Reçu</span>
                      )}
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {email.type === 'sent' ? `→ ${email.to_name || email.to_email}` : `← ${email.from_name || email.from_email}`}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 truncate">{email.subject}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {new Date(email.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedEmail?.id === email.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
                
                {/* Détail de l'email */}
                {selectedEmail?.id === email.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                      {email.body}
                    </pre>
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteEmail(email.id); }}
                        className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm transition"
                      >
                        <Trash2 className="w-4 h-4" />
                        Supprimer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DossierMessaging;
